import { LayoutOutlined } from '@ant-design/icons'
import { t } from 'i18next'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  Badge,
  SmartFunctionRunStatusIcon,
  TableBoldColumnContainer,
} from '../../../components'
import {
  algorithmRunStatuses,
  BadgeColors,
  dateFormat,
  NO_DATA_PLACEHOLDER,
  smartFunctionBadgeStatusColorKeys,
  workspaceTypes,
} from '../../../constants/constants'
import {
  useWorkspaceDetails,
  useWorkspaceSmartFunctionData,
} from '../../../hooks'
import { AlgorithmRunStatus } from '../../../types/generalTypes'
import SmartFunctionActionDropdown from '../../SmartFunctions/components/SmartFunctionActionDropdown/SmartFunctionActionDropdown'
import { ExtendedAlgorithmRun } from '../panels/WorkspaceSmartFunctionPanel'

const TableRow = styled.div<{ $clickable: boolean; $titleRow: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: ${(props) =>
    props.$titleRow ? 'var(--color-bg-mask)' : 'var(--color-bg-base)'};
  width: 100%;
  border-radius: 4px;
  border: 1px solid transparent;
  font-family: Inter;
  line-height: normal;
  font-size: 14px;
  height: 48px;
  &:hover {
    border: 1px solid var(--color-text);
    background-color: var(--color-bg-layout);
  }
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'initial')};
`

const TableCell = styled.div`
  padding: 8px;
  vertical-align: middle;
`

export const NameCell = styled(TableCell)`
  width: 40%;
`
export const OfrIterationFilename = styled(TableCell)`
  width: 15%;
`
export const DateCell = styled(TableCell)`
  width: 15%;
`
export const RunCountCell = styled(TableCell)`
  width: 15%;
`
export const StatusCell = styled(TableCell)`
  width: 15%;
`
export const ActionCell = styled(TableCell)`
  display: flex;
  width: 15%;
  justify-content: flex-end;
`

interface SmartFunctionPanelTableRowProps {
  record: ExtendedAlgorithmRun
  onClick: () => void
}

const SmartFunctionPanelTableRow = ({
  record,
  onClick,
}: SmartFunctionPanelTableRowProps) => {
  const { workspaceId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { isRiskReliefWorkspaceDeviationClosed, getCalculatedRecord } =
    useWorkspaceSmartFunctionData()

  const nameCell = record.isChildRow ? (
    <>{record.smartFunctionName || NO_DATA_PLACEHOLDER}</>
  ) : (
    <TableBoldColumnContainer>
      {record.smartFunctionName || NO_DATA_PLACEHOLDER}
    </TableBoldColumnContainer>
  )

  const ofrIterationFilename = (
    <>
      {record.iterationFilename ? (
        <Badge icon={<LayoutOutlined />} color={BadgeColors.PROCESSING}>
          {record.iterationFilename}
        </Badge>
      ) : (
        NO_DATA_PLACEHOLDER
      )}
    </>
  )

  const dateCell = (
    <>
      {record.actionDate ? (
        <Moment local format={dateFormat.PRIMARY}>
          {record.actionDate}
        </Moment>
      ) : (
        NO_DATA_PLACEHOLDER
      )}
    </>
  )

  const runCountCell = t('smartFunctionDetailsPage.runCount', {
    count: record.algorithmRunIndex || 0,
  })

  const statusCell =
    !record.isTitleRow &&
    (record.calculatedStatus ? (
      <Badge
        color={
          smartFunctionBadgeStatusColorKeys[
            record.calculatedStatus as AlgorithmRunStatus
          ]
        }
        icon={
          <SmartFunctionRunStatusIcon
            status={record.calculatedStatus as AlgorithmRunStatus}
          />
        }
      >
        {t(`workspaceDetailsPage.status.${record.calculatedStatus}`) ||
          algorithmRunStatuses.READY}
      </Badge>
    ) : (
      algorithmRunStatuses.READY
    ))

  const actionCell = !record.isTitleRow && (
    <>
      <SmartFunctionActionDropdown
        key={record.calculatedRecordId}
        algorithmRun={getCalculatedRecord(record)}
        disabled={record.disabled || isRiskReliefWorkspaceDeviationClosed}
      />
    </>
  )

  return (
    <TableRow
      key={record.id || 'row'}
      onClick={onClick}
      $clickable={
        record.id !== '0' &&
        record.calculatedStatus === algorithmRunStatuses.COMPLETE
      }
      $titleRow={!!record.isTitleRow}
    >
      <NameCell>{nameCell}</NameCell>
      {selectedWorkspace?.typeCode === workspaceTypes.OFR_QR && (
        <OfrIterationFilename>{ofrIterationFilename}</OfrIterationFilename>
      )}
      <DateCell>{dateCell}</DateCell>
      <RunCountCell>{runCountCell}</RunCountCell>
      <StatusCell>{statusCell}</StatusCell>
      <ActionCell>{actionCell}</ActionCell>
    </TableRow>
  )
}

export default SmartFunctionPanelTableRow
